import { useEffect, useState } from "react";
import {
  CurrencyDollarIcon,
  CreditCardIcon,
  CalendarCheckIcon,
  WrenchIcon,
  CheckCircleIcon,
  BellIcon,
  PackageIcon,
  ClockIcon,
} from "@phosphor-icons/react";

import KpiTile from "./KpiTile";
import ExportCsvButton from "./ExportCsvButton";
import {
  fetchSummary,
  formatCents,
  formatMinutes,
} from "../../lib/reports";
import { downloadCsv, reportFilename } from "../../lib/csv";

function ackTone(mins) {
  if (mins === null || mins === undefined) return "default";
  if (mins < 30) return "emerald";
  if (mins <= 120) return "amber";
  return "red";
}

export default function SummaryStrip({ params }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchSummary(params)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Failed to load summary");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [params.date_from, params.date_to]);

  const has = !!data;
  const tile = (k, fallback) => (has ? data[k] : fallback);

  const exportCsv = () => {
    if (!has) return;
    const rows = [
      ["MRR", "$" + (data.mrr_cents / 100).toFixed(2), data.mrr_cents],
      ["Collections in range", "$" + (data.payment_collections_cents / 100).toFixed(2), data.payment_collections_cents],
      ["Active subscriptions", data.active_subscriptions, ""],
      ["Open service requests", data.open_service_requests, ""],
      ["Jobs completed in range", data.jobs_completed, ""],
      ["Fleet alerts in range", data.fleet_alert_count, ""],
      ["Open RMAs", data.open_rmas, ""],
      [
        "Avg alert ack (minutes)",
        data.avg_alert_ack_minutes ?? "",
        "",
      ],
    ];
    downloadCsv(
      reportFilename("summary", params),
      ["Metric", "Display value", "Raw cents"],
      rows
    );
  };

  return (
    <div data-testid="reports-summary-strip">
      {err && (
        <div
          data-testid="reports-summary-error"
          className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700"
        >
          {err}
        </div>
      )}
      <div className="mb-2 flex items-center justify-between">
        <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Snapshot — current state + range-bound metrics
        </div>
        <ExportCsvButton
          onExport={exportCsv}
          disabled={!has}
          testId="export-csv-summary"
        />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile
          label="MRR"
          value={formatCents(tile("mrr_cents", 0))}
          Icon={CurrencyDollarIcon}
          loading={loading}
          testId="kpi-mrr"
          hint="Active subs that bill"
        />
        <KpiTile
          label="Collections"
          value={formatCents(tile("payment_collections_cents", 0))}
          Icon={CreditCardIcon}
          loading={loading}
          testId="kpi-collections"
          hint="Paid in selected range"
        />
        <KpiTile
          label="Active Subscriptions"
          value={tile("active_subscriptions", 0)}
          Icon={CalendarCheckIcon}
          loading={loading}
          testId="kpi-active-subs"
        />
        <KpiTile
          label="Open Service Requests"
          value={tile("open_service_requests", 0)}
          Icon={WrenchIcon}
          loading={loading}
          tone={
            (tile("open_service_requests", 0) || 0) > 10 ? "amber" : "default"
          }
          testId="kpi-open-srs"
          hint="Snapshot — current"
        />
        <KpiTile
          label="Jobs Completed"
          value={tile("jobs_completed", 0)}
          Icon={CheckCircleIcon}
          loading={loading}
          testId="kpi-jobs-completed"
          hint="In selected range"
        />
        <KpiTile
          label="Fleet Alerts"
          value={tile("fleet_alert_count", 0)}
          Icon={BellIcon}
          loading={loading}
          testId="kpi-fleet-alerts"
          hint="Created in range"
        />
        <KpiTile
          label="Open RMAs"
          value={tile("open_rmas", 0)}
          Icon={PackageIcon}
          loading={loading}
          tone={(tile("open_rmas", 0) || 0) > 0 ? "amber" : "default"}
          testId="kpi-open-rmas"
        />
        <KpiTile
          label="Avg Alert Ack"
          value={formatMinutes(tile("avg_alert_ack_minutes", null))}
          Icon={ClockIcon}
          loading={loading}
          tone={ackTone(tile("avg_alert_ack_minutes", null))}
          testId="kpi-ack-time"
          hint="Acknowledged in range"
        />
      </div>
    </div>
  );
}
